/**
 * Menu booking mutation. UI is optimistic (store updates on +/-).
 * Do **not** invalidate the menu GET on success — that refetched `menu_builders` on every save and fought local selection state.
 * Pull-to-refresh / next visit still load fresh menu via React Query as usual.
 * On error: invalidate menu query so a refetch can reconcile with server.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { handleMutationError } from '../../query';
import { menuKeys } from './keys';
import { isMenuInvalidationAllowedForDate } from './cache';
import { bookMenu as bookMenuApi, type BookMenuParams } from './api';

function invalidateMenuForBooking(
  query_client: ReturnType<typeof useQueryClient>,
  customer_id: number,
  variables: BookMenuParams,
): void {
  if (typeof customer_id !== 'number') {return;}
  const date_ascii = ensureAsciiDateString(variables.date) || variables.date;
  if (!isMenuInvalidationAllowedForDate(date_ascii)) {
    return;
  }
  const key = menuKeys.day(customer_id, date_ascii, variables.is_double_box);
  query_client.invalidateQueries({ queryKey: key });
}

export function useMenuBooking() {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);

  const mutation = useMutation({
    mutationFn: (params: BookMenuParams) => bookMenuApi(params),
    onSuccess: () => {},
    onError: (err, variables) => {
      handleMutationError(err, { mutation: 'bookMenu', date: variables?.date });
      if (typeof customer_id === 'number') {
        invalidateMenuForBooking(query_client, customer_id, variables);
      }
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
